/**
 * Collection Page JavaScript
 * Untamed v2.1 - Collection Section
 * Handles filter drawer, sorting, and pagination
 */

(function() {
  'use strict';

  // ===== Filter apply / clear (inside the shared collection-filters ui-sheet) =====
  const filterApply = document.getElementById('filter-apply');
  const filterClear = document.getElementById('filter-clear');

  // ===== Filter Form Handling =====
  const filterForm = document.getElementById('filter-form');
  
  function getActiveFilters() {
    const url = new URL(window.location);
    const filters = {};
    
    // Parse filter params
    for (const [key, value] of url.searchParams) {
      if (key.startsWith('filter.')) {
        const filterKey = key.replace('filter.', '');
        if (!filters[filterKey]) {
          filters[filterKey] = [];
        }
        filters[filterKey].push(value);
      }
    }
    
    return filters;
  }

  function buildFilterUrl(filters) {
    const url = new URL(window.location);
    
    // Remove all existing filter params
    const keysToDelete = [];
    for (const key of url.searchParams.keys()) {
      if (key.startsWith('filter.')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => url.searchParams.delete(key));
    
    // Add new filter params
    for (const [key, values] of Object.entries(filters)) {
      values.forEach(value => {
        url.searchParams.append(`filter.${key}`, value);
      });
    }
    
    // Reset to page 1 when filtering
    url.searchParams.delete('page');
    
    return url.toString();
  }

  // Apply filters
  filterApply?.addEventListener('click', () => {
    const form = document.getElementById('filter-form');
    if (!form) return;
    
    const filters = {};
    const formData = new FormData(form);

    for (const [key, value] of formData) {
      // Only Shopify filter inputs, and strip the "filter." prefix because
      // buildFilterUrl() re-adds it. Without stripping, keys double up as
      // "filter.filter.v.price.gte" and Shopify ignores them (price and
      // availability filters silently do nothing). Mirrors the sidebar apply().
      if (!value || key.indexOf('filter.') !== 0) continue;
      const filterKey = key.replace('filter.', '');
      if (!filters[filterKey]) {
        filters[filterKey] = [];
      }
      filters[filterKey].push(value);
    }

    window.location = buildFilterUrl(filters);
  });

  // Clear all filters
  filterClear?.addEventListener('click', () => {
    const url = new URL(window.location);
    
    // Remove all filter params
    const keysToDelete = [];
    for (const key of url.searchParams.keys()) {
      if (key.startsWith('filter.')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => url.searchParams.delete(key));
    
    window.location = url.toString();
  });

  // Remove individual filter
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-remove-filter]');
    if (!btn) return;

    const filterValue = btn.dataset.filterValue;
    let filterKey = btn.dataset.filterKey || '';

    // Support both "filter.p.product_type" and "p.product_type"
    if (filterKey.startsWith('filter.')) {
      filterKey = filterKey.replace('filter.', '');
    }

    const filters = getActiveFilters();

    if (filters[filterKey]) {
      filters[filterKey] = filters[filterKey].filter(v => v !== filterValue);
      if (filters[filterKey].length === 0) {
        delete filters[filterKey];
      }
    }

    window.location = buildFilterUrl(filters);
  });

  // Clear all filters button in toolbar
  document.querySelector('.collection-toolbar__clear-filters')?.addEventListener('click', () => {
    const url = new URL(window.location);
    
    const keysToDelete = [];
    for (const key of url.searchParams.keys()) {
      if (key.startsWith('filter.')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => url.searchParams.delete(key));
    
    window.location = url.toString();
  });

  // ===== Sort Handling =====
  // Bind every sort control (toolbar select + the in-sheet mobile select).
  document.querySelectorAll('#sort-select, [data-sort-select]').forEach((sel) => {
    sel.addEventListener('change', () => {
      const url = new URL(window.location);
      url.searchParams.set('sort_by', sel.value);
      url.searchParams.delete('page'); // Reset to page 1 when sorting
      window.location = url.toString();
    });
  });

  // ===== Load More / Infinite Scroll =====
  let loadMoreBtn = null;
  let isLoading = false;

  async function loadMoreProducts() {
    if (isLoading) return;
    
    const nextPageUrl = loadMoreBtn?.dataset.nextUrl;
    if (!nextPageUrl) return;
    
    isLoading = true;
    loadMoreBtn.classList.add('is-loading');
    loadMoreBtn.textContent = 'Loading...';
    
    try {
      const response = await fetch(nextPageUrl);
      const html = await response.text();
      
      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get new products
      const newProducts = doc.querySelectorAll('#product-grid .product-card');
      const newLoadMoreBtn = doc.getElementById('load-more-btn');
      
      // Get reference to product grid
      const productGrid = document.getElementById('product-grid');
      
      // Append new products
      newProducts.forEach(product => {
        productGrid?.appendChild(product.cloneNode(true));
      });
      
      // Update load more button
      if (newLoadMoreBtn) {
        loadMoreBtn.dataset.nextUrl = newLoadMoreBtn.dataset.nextUrl;
        loadMoreBtn.textContent = 'Load More';
        loadMoreBtn.classList.remove('is-loading');
      } else {
        loadMoreBtn.remove();
      }
      
      // Reinitialize Phosphor icons if needed
      if (window.UntamedIcons && typeof window.UntamedIcons.refresh === 'function') {
        window.UntamedIcons.refresh();
      }
      
      // Reinitialize product card behavior for newly added cards
      document.dispatchEvent(new CustomEvent('product-card:updated'));
      
    } catch (error) {
      console.error('Failed to load more products:', error);
      loadMoreBtn.textContent = 'Load More';
      loadMoreBtn.classList.remove('is-loading');
    }
    
    isLoading = false;
  }

  // Initialize Load More and Infinite Scroll after DOM is ready
  function initPagination() {
    loadMoreBtn = document.getElementById('load-more-btn');
    
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', loadMoreProducts);
    }

    // ===== Infinite Scroll =====
    const infiniteScrollTrigger = document.getElementById('infinite-scroll-trigger');
    
    if (infiniteScrollTrigger) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !isLoading) {
            loadMoreProducts();
          }
        });
      }, {
        rootMargin: '200px'
      });
      
      observer.observe(infiniteScrollTrigger);
    }
  }

  // Run pagination init when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPagination);
  } else {
    initPagination();
  }

  // ===== Filter Group Toggle (Details) =====
  document.querySelectorAll('.filter-group').forEach(group => {
    const summary = group.querySelector('.filter-group__header');
    const icon = group.querySelector('.filter-group__icon');
    
    group.addEventListener('toggle', () => {
      // Icon is handled by CSS ::after
    });
  });

  // ===== Price Range Inputs =====
  const priceMinInput = document.querySelector('[data-price-min]');
  const priceMaxInput = document.querySelector('[data-price-max]');
  
  function updatePriceFilter() {
    const min = priceMinInput?.value || '';
    const max = priceMaxInput?.value || '';
    
    // Update hidden inputs for form submission. Shopify's filter.v.price.gte/lte
    // expect the shop's MAJOR currency unit (e.g. 1000 for Rs 1000), NOT cents.
    const hiddenMin = document.getElementById('filter-price-min');
    const hiddenMax = document.getElementById('filter-price-max');

    if (hiddenMin) hiddenMin.value = min ? min : '';
    if (hiddenMax) hiddenMax.value = max ? max : '';
  }
  
  priceMinInput?.addEventListener('change', updatePriceFilter);
  priceMaxInput?.addEventListener('change', updatePriceFilter);

  // ===== Filter sheet color swatches (reference) =====
  document.querySelectorAll('.filter-swatch').forEach(label => {
    var cb = label.querySelector('input[type=checkbox]');
    if (!cb) return;
    label.addEventListener('click', function () {
      // native label toggles cb; sync visual class next tick
      setTimeout(function () { label.classList.toggle('selected', cb.checked); }, 0);
    });
  });

  // ===== Color Swatch Selection =====
  document.querySelectorAll('.filter-color').forEach(swatch => {
    swatch.addEventListener('click', () => {
      swatch.classList.toggle('filter-color--selected');
      
      // Update associated checkbox
      const checkbox = swatch.querySelector('input[type="checkbox"]');
      if (checkbox) {
        checkbox.checked = swatch.classList.contains('filter-color--selected');
      }
    });
  });

  // ===== Mobile view switcher: card grid <-> full-width PCH list =====
  (function initViewToggle() {
    var toggle = document.querySelector('[data-view-toggle]');
    var views = document.querySelector('[data-cpg-views]');
    if (!toggle || !views) return;

    function applyView(mode, persist) {
      mode = (mode === 'list') ? 'list' : 'grid';
      views.setAttribute('data-view-mode', mode);
      var list = views.querySelector('[data-cpg-list]');
      if (list) list.setAttribute('aria-hidden', mode === 'list' ? 'false' : 'true');
      toggle.querySelectorAll('[data-view]').forEach(function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-view') === mode);
      });
      if (persist) { try { localStorage.setItem('collection:viewmode', mode); } catch (e) {} }
    }

    var def = toggle.getAttribute('data-default-view') || 'grid';
    var saved = def;
    try { saved = localStorage.getItem('collection:viewmode') || def; } catch (e) {}
    applyView(saved, false);

    toggle.querySelectorAll('[data-view]').forEach(function (b) {
      b.addEventListener('click', function () {
        var mode = b.getAttribute('data-view');
        var from = views.getAttribute('data-view-mode');
        applyView(mode, true);
        // Animate the cards of the now-active view in.
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && window.gsap) {
          var active = mode === 'list' ? views.querySelector('[data-cpg-list]') : document.getElementById('product-grid');
          var items = active ? active.querySelectorAll('.product-card, .product-card-horizontal, [data-cpg-list-item]') : [];
          if (items.length) {
            window.gsap.fromTo(items, { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.4, ease: 'power2.out', stagger: 0.03, overwrite: true });
          }
        }
        emitCollection('collection_view_density_changed', { from_view: from, to_view: mode });
      });
    });
  })();

  // ===== Sidebar filter layout: auto-apply on change =====
  (function initSidebarFilters() {
    var sidebar = document.querySelector('.collection-sidebar form[data-collection-filters]');
    if (!sidebar) return;
    function syncPrice() {
      var minV = sidebar.querySelector('[data-price-min]');
      var maxV = sidebar.querySelector('[data-price-max]');
      sidebar.querySelectorAll('input[type=hidden]').forEach(function (h) {
        if (!/price/i.test(h.name)) return;
        // Shopify's filter.v.price.gte/lte take the MAJOR currency unit, not cents.
        if (/gte|min/i.test(h.name) && minV) h.value = minV.value ? parseFloat(minV.value) : '';
        if (/lte|max/i.test(h.name) && maxV) h.value = maxV.value ? parseFloat(maxV.value) : '';
      });
    }
    function apply() {
      syncPrice();
      var filters = {};
      new FormData(sidebar).forEach(function (v, k) {
        if (!v || k.indexOf('filter.') !== 0) return;
        var key = k.replace('filter.', '');
        (filters[key] = filters[key] || []).push(v);
      });
      window.location = buildFilterUrl(filters);
    }
    sidebar.addEventListener('change', apply);
    var clear = document.getElementById('filter-clear-sidebar');
    if (clear) clear.addEventListener('click', function () { window.location = window.location.pathname; });
  })();

  // ===== Filter button: toggle the sidebar column on desktop, open the sheet on mobile =====
  (function initFilterButton() {
    var btn = document.getElementById('filter-toggle');
    if (!btn) return;
    var sheetId = btn.getAttribute('data-ui-sheet-id') || 'collection-filters';
    var layout = document.querySelector('.collection-page--filters-sidebar .cpg-layout');
    var desktop = window.matchMedia('(min-width: 990px)');
    // Restore persisted collapsed state.
    if (layout) {
      try { if (localStorage.getItem('collection:filters-hidden') === '1') layout.classList.add('is-filters-hidden'); } catch (e) {}
    }
    btn.addEventListener('click', function (e) {
      if (layout && desktop.matches) {
        e.preventDefault();
        var hidden = layout.classList.toggle('is-filters-hidden');
        btn.setAttribute('aria-pressed', hidden ? 'false' : 'true');
        try { localStorage.setItem('collection:filters-hidden', hidden ? '1' : '0'); } catch (e2) {}
        if (window.ScrollTrigger && window.ScrollTrigger.refresh) window.ScrollTrigger.refresh();
      } else if (window.UISheet) {
        window.UISheet.open(sheetId);
      }
    });
  })();

  // ===== Price dual-range slider =====
  // Two overlaid range inputs drive the fill bar, the number inputs and the
  // hidden filter inputs. Self-contained per form (works in both the sidebar
  // and the filter sheet). The sidebar auto-applies on release; the sheet waits
  // for its "Show N" button which reads the synced hidden inputs.
  function initPriceSliders(root) {
    (root || document).querySelectorAll('[data-price-slider]').forEach(function (slider) {
      if (slider.__priceInit) return;
      slider.__priceInit = true;
      var lo = parseFloat(slider.getAttribute('data-min')) || 0;
      var hi = parseFloat(slider.getAttribute('data-max')) || 0;
      if (hi <= lo) return;
      var tMin = slider.querySelector('[data-price-thumb-min]');
      var tMax = slider.querySelector('[data-price-thumb-max]');
      var fill = slider.querySelector('[data-price-fill]');
      var form = slider.closest('form');
      if (!tMin || !tMax || !form) return;
      var nMin = form.querySelector('[data-price-min]');
      var nMax = form.querySelector('[data-price-max]');
      var hMin = nMin && nMin.parentElement.querySelector('input[type="hidden"]');
      var hMax = nMax && nMax.parentElement.querySelector('input[type="hidden"]');

      function pct(v) { return ((v - lo) / (hi - lo)) * 100; }
      function paint() {
        var a = parseFloat(tMin.value), b = parseFloat(tMax.value);
        fill.style.left = pct(a) + '%';
        fill.style.right = (100 - pct(b)) + '%';
      }
      // Always keep the hidden filter.v.price.gte/lte inputs in sync with the
      // thumbs. A value sitting at the range edge means "no bound", so clear it.
      // Shopify expects the MAJOR currency unit (e.g. 1000 for Rs 1000), not
      // cents — the thumb values (a/b) are already in major units.
      function syncHidden() {
        var a = parseFloat(tMin.value), b = parseFloat(tMax.value);
        if (hMin) hMin.value = a > lo ? a : '';
        if (hMax) hMax.value = b < hi ? b : '';
      }
      // Write the visible number fields FROM the thumbs. Only used by the slider
      // drag and on commit (blur/enter) — never while the user is mid-type.
      function syncNums() {
        var a = parseFloat(tMin.value), b = parseFloat(tMax.value);
        if (nMin) nMin.value = a > lo ? a : '';
        if (nMax) nMax.value = b < hi ? b : '';
      }
      function onThumb(e) {
        var a = parseFloat(tMin.value), b = parseFloat(tMax.value);
        if (a > b) {
          if (e.target === tMin) { tMin.value = b; } else { tMax.value = a; }
        }
        paint();
        syncNums();
        syncHidden();
      }
      tMin.addEventListener('input', onThumb);
      tMax.addEventListener('input', onThumb);
      // On release, let the form's change-based apply (sidebar) pick it up.
      function commit() { if (nMin) nMin.dispatchEvent(new Event('change', { bubbles: true })); }
      tMin.addEventListener('change', commit);
      tMax.addEventListener('change', commit);
      // Typing in the number fields moves the thumbs + hidden inputs, but must
      // NOT rewrite the field being typed into. The old shared syncOut() clamped
      // each partial keystroke to the range edge and blanked the field, so the
      // user could never enter a value and the hidden price inputs stayed empty
      // (no price filter was ever submitted). An empty field resets that bound.
      function fromNums() {
        tMin.value = (nMin && nMin.value !== '') ? Math.max(lo, Math.min(parseFloat(nMin.value) || lo, hi)) : lo;
        tMax.value = (nMax && nMax.value !== '') ? Math.max(lo, Math.min(parseFloat(nMax.value) || hi, hi)) : hi;
        paint();
        syncHidden();
      }
      if (nMin) nMin.addEventListener('input', fromNums);
      if (nMax) nMax.addEventListener('input', fromNums);
      // On commit (blur / Enter) snap the visible fields back into range.
      if (nMin) nMin.addEventListener('change', syncNums);
      if (nMax) nMax.addEventListener('change', syncNums);
      paint();
    });
  }

  // ===== GSAP card reveal (entrance + scroll-batch) =====
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function whenGsap(cb) {
    if (window.gsap) return cb(window.gsap);
    if (window.gsapReady && window.gsapReady.then) window.gsapReady.then(cb).catch(function () {});
  }

  // GSAP loads lazily (interaction / 2s timeout). For the entrance we only want
  // to hide+reveal cards if GSAP is ready promptly — otherwise we'd blink the
  // already-visible cards out and back in. Resolves with gsap or null.
  function gsapSoon(ms) {
    return new Promise(function (resolve) {
      if (window.gsap) return resolve(window.gsap);
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; resolve(null); } }, ms || 700);
      if (window.gsapReady && window.gsapReady.then) {
        window.gsapReady.then(function (g) { if (!done) { done = true; clearTimeout(t); resolve(g || window.gsap || null); } });
      }
    });
  }

  function revealCards(cards) {
    if (!cards || !cards.length || reduceMotion) return;
    whenGsap(function (gsap) {
      gsap.fromTo(cards,
        { autoAlpha: 0, y: 24 },
        { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.045, overwrite: true });
    });
  }

  function initGridReveal() {
    var grid = document.getElementById('product-grid');
    if (!grid || reduceMotion) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('.product-card'));
    if (!cards.length) return;
    gsapSoon(700).then(function (gsap) {
      if (!gsap) return; // GSAP too slow — leave cards visible, no entrance.
      var ST = window.ScrollTrigger;
      if (ST && gsap.registerPlugin) { try { gsap.registerPlugin(ST); } catch (e) {} }
      if (ST && ST.batch) {
        gsap.set(cards, { autoAlpha: 0, y: 24 });
        ST.batch(cards, {
          start: 'top 92%',
          onEnter: function (batch) {
            gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.045, overwrite: true });
          }
        });
        ST.refresh();
      } else {
        revealCards(cards.slice(0, 12));
      }
    });
  }

  // Reveal newly appended cards on load-more / infinite scroll.
  document.addEventListener('product-card:updated', function () {
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    var fresh = grid.querySelectorAll('.product-card:not([data-revealed])');
    fresh.forEach(function (c) { c.setAttribute('data-revealed', ''); });
    revealCards(Array.prototype.slice.call(fresh));
  });

  // ===== Analytics helper =====
  function emitCollection(name, detail) {
    try {
      var base = {};
      if (window.meta && window.meta.product) {} // noop
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, detail || {}));
      if (window.Shopify && window.Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, detail || {});
    } catch (e) {}
  }
  // expose for inline handlers
  window.__collectionEmit = emitCollection;

  // ===== Initialize =====
  function init() {
    // Initialize Phosphor icons
    if (window.UntamedIcons && typeof window.UntamedIcons.refresh === 'function') {
      window.UntamedIcons.refresh();
    }
    var grid = document.getElementById('product-grid');
    if (grid) grid.querySelectorAll('.product-card').forEach(function (c) { c.setAttribute('data-revealed', ''); });
    initGridReveal();
    initPriceSliders();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

